import { FleetImporter } from "@/services/fleet-importer";
import { ResearchId } from "@/helpers/research-id";
import Research from "@/store/model/research";
import { default as store } from "@/store/index";
import SpecialShipData from "@/store/model/special-ship-data";
import Specialist from "@/store/model/specialist";

// matches the contents of a battle report that are relevant to extraction
const BattleReportPattern = /(?:Angreifer(?:.*\n)+?)?[ \t]*Angreifende Flotte(?:.*\n)+?[ \t]*Verteidigende Flotte(?:.*\n)+?[ \t]*Daten des Angreifers(?:.*\n)+?[ \t]*Daten des Verteidigers(?:.*\n)+?Einheiten/i;
// const BattleReportPattern = /(?:Kampfbericht\nvom Planeten ([0-9]+\/[0-9]+ : [0-9]+\/[0-9]+ : [0-9]+)? ?(.*)[ \t\n]+Zeit: (.*), (.*)[ \t\n]+)?Angreifer: (.*)[ \t\n]+?Verteidiger: (.*)[ \t\n]+Angreifende Flotte\n((?:.* *\t?[0-9`]+\n)+|Nichts vorhanden!\n)[ \t\n]+Verteidigende Flotte[ \t\n]+((?:.* *\t?[0-9`]+\n)+|Nichts vorhanden!\n)[ \t\n]+Planetare Verteidigung[ \t\n]+((?:.* *\t?[0-9`]+\n)+|Nichts vorhanden!\n)[ \t\n]+Daten des Angreifers[ \t\n]+((?:.* \([0-9]+%\): ?\t?[0-9`]+\n)+)Einheiten:[ \t\n]+[0-9`]+[ \t\n]+Daten des Verteidigers[ \t\n]+((?:.* \([0-9]+%\): ?\t?[0-9`]+\n)+)Einheiten:[ \t]+[0-9`]+[ \t\n]+(?:.*\n)+/i;

const PlanetPattern = /vom Planeten ([0-9]+\/[0-9]+ : [0-9]+\/[0-9]+ : [0-9]+)? ?(.*)/i;
const DatePattern = /Zeit: (.*), (.*)/i;
const AttackerPattern = /Angreifer: ((?:(?![\t ]*(?:Verteidiger:|\n)).)*)/i;
const DefenderPattern = /Verteidiger: (.*)/i;
const AttackerFleetPattern = /Angreifende Flotte.*[ \t\n]+((?:.* *\t?[0-9`]+[ \t]*\n)+|Nichts vorhanden!\n)/i;
const DefenderFleetPattern = /Verteidigende Flotte.*[ \t\n]+((?:.* *\t?[0-9`]+[ \t]*\n)+|Nichts vorhanden!\n)/i;
const DefenderDefensesPattern = /Planetare Verteidigung.*[ \t\n]+((?:.* *\t?[0-9`]+[ \t]*\n)+|Nichts vorhanden!\n)/i;
const AttackerBuffsPattern = /Daten des Angreifers.*[ \t\n]+((?:.* \([0-9]+%\).*\n)+)[ \t\n]*Einheiten/i;
const DefenderBuffsPattern = /Daten des Verteidigers.*[ \t\n]+((?:.* \([0-9]+%\).*\n)+)[ \t\n]*Einheiten/i;
const DefenderRestorePattern = /([0-9]+)% der Verteidigungsanlagen werden repariert/;

const ObjectEntryPattern = /(.*)[ \t]+([0-9`]+[ \t]*)\n/;
const BuffEntryPattern = /(.*) \(([0-9]+)%\):.*\n/;

const BattleReportImporter = {
  import(data, role) {
    const battleReportMatch = BattleReportPattern.exec(data);
    if (!battleReportMatch) {
      return null;
    }
    const battleReportString = battleReportMatch[0];

    const planetMatch = battleReportString.match(PlanetPattern);
    const dateMatch = battleReportString.match(DatePattern);
    const attackerMatch = battleReportString.match(AttackerPattern);
    const defenderMatch = battleReportString.match(DefenderPattern);
    const attackerFleetMatch = battleReportString.match(AttackerFleetPattern);
    const defenderFleetMatch = battleReportString.match(DefenderFleetPattern);
    const defenderDefensesMatch = battleReportString.match(
      DefenderDefensesPattern
    );
    const attackerBuffsMatch = battleReportString.match(AttackerBuffsPattern);
    const defenderBuffsMatch = battleReportString.match(DefenderBuffsPattern);
    const defenderRestoreMatch = data.match(DefenderRestorePattern);

    const planetCoordinates = planetMatch ? planetMatch[1] : "";
    const planetName = planetMatch ? planetMatch[2] : "";
    const date = dateMatch ? dateMatch[1] : "";
    const time = dateMatch ? dateMatch[2] : "00-00-00";
    const attackerName = attackerMatch ? attackerMatch[1] : "";
    const defenderName = defenderMatch ? defenderMatch[1] : "";

    const dateParts =
      date != null && date.length > 0 ? date.split(".") : ["00", "00", "0000"];
    const reportTime = Date.parse(
      dateParts[2] +
        "-" +
        dateParts[1] +
        "-" +
        dateParts[0] +
        "T" +
        time +
        ".000+01:00"
    );

    const shipTypes = store.getters["data/shipTypes"];

    const attackerShips = attackerFleetMatch
      ? FleetImporter.importData(attackerFleetMatch[1], ObjectEntryPattern)
      : [];
    const defenderShips = defenderFleetMatch
      ? FleetImporter.importData(defenderFleetMatch[1], ObjectEntryPattern)
      : [];
    const defenderDefenses = defenderDefensesMatch
      ? FleetImporter.importData(defenderDefensesMatch[1], ObjectEntryPattern)
      : [];

    const attackerResearch = attackerBuffsMatch
      ? this.importResearch(
          attackerBuffsMatch[1],
          attackerShips
            .filter(s => shipTypes[s.typeId].special != null)
            .map(s => [s, shipTypes[s.typeId]]),
          BuffEntryPattern
        )
      : [];
    const defenderResearch = defenderBuffsMatch
      ? this.importResearch(
          defenderBuffsMatch[1],
          defenderShips
            .filter(s => shipTypes[s.typeId].special != null)
            .map(s => [s, shipTypes[s.typeId]]),
          BuffEntryPattern
        )
      : [];
    const specialists = [];
    if (
      role === "defender" &&
      defenderRestoreMatch != null &&
      parseInt(defenderRestoreMatch[1]) > 40
    ) {
      specialists.push(new Specialist("etoa:engineer"));
    }

    if (role === "attacker") {
      return {
        playerName: attackerName,
        items: attackerShips.concat(attackerResearch)
      };
    }

    return {
      playerName: defenderName,
      planetCoordinates: planetCoordinates,
      planetName: planetName,
      plannedTime: reportTime,
      items: defenderShips
        .concat(defenderDefenses)
        .concat(defenderResearch)
        .concat(specialists)
    };
  },
  importResearch(data, ships, entryPattern) {
    let entryMatches = [];
    let entryMatch;
    while ((entryMatch = entryPattern.exec(data)) !== null) {
      entryMatches.push(entryMatch);
      data = data.substring(entryMatch.index + entryMatch[0].length);
    }
    const researchEntries = [];
    const levels = [];
    // first just write down what research there is and what levels they have at most
    entryMatches.forEach(m => {
      const buffName = m[1];
      const amount = parseInt(m[2].replace("`", ""));
      const typeId = this.getResearchTypeId(buffName);
      if (typeId == null) {
        return;
      }

      let level = Math.floor((amount - 100) / 10);
      researchEntries.push([new Research(typeId, level), amount]);
      if (level > 0) {
        levels.push(level);
      }
    });
    if (ships.find(s => s.special != null) == null) {
      return researchEntries.map(e => e[0]);
    }
    const averageLevel =
      levels.reduce((a, b) => a + b, 0) / Math.max(1, levels.length);
    const result = [];
    // then iterate over them and figure out the situation, assigning remaining buffs to special ships
    // also, reduce levels on exceptionally high research as they are probably compensated by special ship buffs.
    researchEntries.forEach(entry => {
      const research = entry[0];
      const typeId = research.typeId;
      const amount = entry[1];
      let level = Math.min(research.level, Math.ceil(averageLevel));
      let remainingBuff = amount - (100 + level * 10);
      const specialShipsSorted = ships
        .filter(e => e[1].special.boni_per_level[typeId] != null)
        .sort((a, b) =>
          a[1].special.boni_per_level[typeId] <
          b[1].special.boni_per_level[typeId]
            ? 1
            : a[1].special.boni_per_level[typeId] >
              b[1].special.boni_per_level[typeId]
            ? -1
            : 0
        );
      if (specialShipsSorted.length > 0) {
        const lastSpecialShip =
          specialShipsSorted[specialShipsSorted.length - 1]; // the last special ship in the list has the lowest bonus per level so it is the easiest to spread levels
        if (remainingBuff > lastSpecialShip[1].special.boni_per_level[typeId]) {
          level--;
          remainingBuff += 10;
        }
      }
      research.level = level;
      result.push(research);
      if (remainingBuff === 0 || ships.length === 0) {
        return;
      }

      for (let i = 0; i < specialShipsSorted.length; i++) {
        const ship = specialShipsSorted[i][0];
        const shipType = specialShipsSorted[i][1];
        const bonusPerLevel = shipType.special.boni_per_level[typeId] * 100;
        if (ship.special == null) {
          ship.special = new SpecialShipData();
        }
        let level = Math.floor(remainingBuff / bonusPerLevel);
        if (i + 1 < specialShipsSorted.length) {
          const nextShipBonusPerLevel =
            specialShipsSorted[i + 1][1].special.boni_per_level[typeId] * 100;
          let nextRemainingBuff = remainingBuff - level * bonusPerLevel;
          while (
            level > 0 &&
            (nextRemainingBuff / nextShipBonusPerLevel) % 1 !== 0
          ) {
            level--;
            nextRemainingBuff = remainingBuff - level * bonusPerLevel;
          }
        }
        ship.special.setBonusLevel(typeId, level);
        remainingBuff -= level * bonusPerLevel;
        if (remainingBuff <= 0) {
          break;
        }
      }
    });
    return result;
  },
  getResearchTypeId(buffName) {
    switch (buffName) {
      case "Schild":
        return ResearchId.SHIELDS;
      case "Struktur":
        return ResearchId.ARMOR;
      case "Waffen":
        return ResearchId.WEAPONS;
      case "Heilung":
        return ResearchId.REGENERATION;
      default:
        return null;
    }
  }
};

export { BattleReportImporter };
